import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchBatchTestStudents, fetchPresignedUrls } from '../shared/api';
import { InlineSpinner } from '../shared/Spinner';
import { loadFont, cssFontFamily } from '../shared/fontLoader';

// Fall 2027 has no real poses yet — sample from Fall 2026 for now.
const SAMPLE_SEASON = 'fall2026';
const CELL_W_PORTRAIT = 200;
const CELL_H_PORTRAIT = 250;
const CELL_W_LANDSCAPE = 260;
const CELL_H_LANDSCAPE = 170;

const cellSize = (slotType) => (
  slotType === 'landscape'
    ? { w: CELL_W_LANDSCAPE, h: CELL_H_LANDSCAPE }
    : { w: CELL_W_PORTRAIT,  h: CELL_H_PORTRAIT }
);

// One (student × slot) cell: bg image behind, pose over it, look's overlay on
// top, text fields laid out. All DOM/CSS — no canvas — so it works with public
// S3 URLs for backgrounds without needing CORS. Pose thumbnails come pre-signed
// from the API and are drawn via <img>.
const BatchCell = ({ slot, look, student, backgroundUrl, overlayUrl }) => {
  const { w, h } = cellSize(slot.slot_type);
  const canvasH = look?.canvas_height || 3000;

  if (!look) {
    return (
      <div style={{ width: w, height: h, background: '#f5f5f5', border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#999', borderRadius: 4 }}>
        No look assigned
      </div>
    );
  }

  // look.pose_number is stored as a spec-suffix string like "-13" (see
  // POSE_NUMBERS in LookTab.jsx); pose.number from the API is an integer.
  // Strip the leading dash and parse to match.
  const targetPoseNum = look.pose_number != null
    ? parseInt(String(look.pose_number).replace(/^-/, ''), 10)
    : null;
  const pose = Number.isFinite(targetPoseNum)
    ? (student.poses || []).find((p) => p.number === targetPoseNum)
    : null;

  return (
    <div style={{
      width: w, height: h, position: 'relative', overflow: 'hidden', borderRadius: 4,
      background: backgroundUrl ? `url(${backgroundUrl}) center/cover no-repeat` : '#eee',
      border: '1px solid #ddd',
    }}
    >
      {pose?.thumbnail_url ? (
        <img
          src={pose.thumbnail_url}
          alt=""
          crossOrigin="anonymous"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, color: '#e65100', background: 'rgba(255, 243, 224, 0.9)', textAlign: 'center', padding: 6,
        }}
        >
          {look.pose_number != null ? `No Pose ${look.pose_number} for this student` : 'Look has no pose_number'}
        </div>
      )}
      {overlayUrl && (
        <img
          src={overlayUrl}
          alt=""
          crossOrigin="anonymous"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        />
      )}
      {(look.text_fields || []).map((tf, i) => {
        if (tf.center_x == null || tf.center_y == null) return null;
        const leftPct = (tf.bound_x_left ?? 0) * 100;
        const rightPct = (tf.bound_x_right ?? 1) * 100;
        const widthPct = Math.max(1, rightPct - leftPct);
        const centerYPct = tf.center_y * 100;
        // Font sized off the smaller cell height, mirroring the LookCanvas's
        // scaling logic. Uses a reasonable cell-sized text (up to ~22px) — the
        // point is to check placement, not pixel-perfect print output.
        const fontPx = Math.min(22, Math.max(8, (tf.font_size_max || 160) * h / canvasH));
        const substituted = (tf.content || '').replace(/\{\{first_name\}\}/g, (student.student_name || '').split(' ')[0] || '')
          .replace(/\{\{last_name\}\}/g, (student.student_name || '').split(' ').slice(1).join(' '))
          .replace(/\{\{full_name\}\}/g, student.student_name || '')
          .replace(/\{\{grade\}\}/g, '')
          .replace(/\{\{school_name\}\}/g, '');
        return (
          <div
            key={tf.id ?? `${i}`}
            style={{
              position: 'absolute',
              left: `${leftPct}%`, width: `${widthPct}%`,
              top: `${centerYPct}%`, transform: 'translateY(-50%)',
              textAlign: tf.align || 'center',
              fontFamily: cssFontFamily(tf.font_filename),
              fontSize: fontPx,
              fontWeight: tf.bold ? 700 : 400,
              fontStyle: tf.italic ? 'italic' : 'normal',
              color: tf.font_color || '#000',
              textShadow: tf.shadow ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip',
              pointerEvents: 'none',
              lineHeight: 1.1,
            }}
          >
            {substituted}
          </div>
        );
      })}
    </div>
  );
};

const BatchTest = ({ slots, looks, backgroundsById, layoutSeason, onClose }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayUrls, setOverlayUrls] = useState({});
  // 'by_student' → one row per kid, all their slots side-by-side.
  // 'by_look'    → one row per slot, all kids' takes side-by-side (transpose).
  const [viewMode, setViewMode] = useState('by_student');

  const nonBuyAllSlots = useMemo(
    () => (slots || []).filter((s) => s.slot_type !== 'buy_all'),
    [slots],
  );

  const shuffle = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchBatchTestStudents(SAMPLE_SEASON, 8)
      .then((data) => setStudents(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.response?.data?.error || err.message || 'Failed to load students'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { shuffle(); }, [shuffle]);

  // Batch-fetch presigned URLs for every look overlay used in the layout.
  useEffect(() => {
    const keys = new Set();
    nonBuyAllSlots.forEach((slot) => {
      const look = (looks || []).find((l) => l.id === slot.look_template_id);
      if (look?.overlay_s3_path && look.overlay_filename) {
        const suffix = look.overlay_s3_path.endsWith('/') ? '' : '/';
        keys.add(`${look.overlay_s3_path}${suffix}${look.overlay_filename}`);
      }
    });
    if (keys.size === 0) return;
    const keyList = Array.from(keys);
    fetchPresignedUrls(keyList)
      .then((urls) => setOverlayUrls((prev) => ({ ...prev, ...urls })))
      .catch(() => {});
  }, [nonBuyAllSlots, looks]);

  // Kick off font loads for every text field in the layout. cssFontFamily
  // already emits the custom family with a sans-serif fallback, so the DOM
  // will swap to the real font the moment FontFace.load() resolves — no
  // manual re-render needed.
  useEffect(() => {
    nonBuyAllSlots.forEach((slot) => {
      const look = (looks || []).find((l) => l.id === slot.look_template_id);
      (look?.text_fields || []).forEach((tf) => {
        if (tf.font_filename) loadFont(tf.font_filename, tf.font_s3_path);
      });
    });
  }, [nonBuyAllSlots, looks]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, width: '95%', maxWidth: 1600, maxHeight: '95vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '12px 20px', borderBottom: '1px solid #e0e0e0',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>Batch Test</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Sampling {SAMPLE_SEASON}
            {layoutSeason && layoutSeason !== SAMPLE_SEASON && (
              <span style={{ marginLeft: 8, color: '#f57c00' }}>
                (layout is {layoutSeason} — real poses not available yet, using {SAMPLE_SEASON})
              </span>
            )}
            {' • '}
            {students.length} student{students.length === 1 ? '' : 's'}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: '#666' }}>View:</span>
            {[
              { id: 'by_student', label: 'By Student' },
              { id: 'by_look',    label: 'By Look' },
            ].map((opt) => {
              const active = viewMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setViewMode(opt.id)}
                  style={{
                    padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                    border: `1px solid ${active ? '#1976d2' : '#ccc'}`,
                    background: active ? '#e3f2fd' : '#fff',
                    color: active ? '#0d47a1' : '#555',
                    borderRadius: 3, fontWeight: active ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={shuffle}
            disabled={loading}
            style={{
              padding: '6px 14px', border: 'none', borderRadius: 4,
              background: loading ? '#bbb' : '#1976d2', color: '#fff',
              cursor: loading ? 'default' : 'pointer', fontSize: 13,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {loading ? <InlineSpinner size={12} color="#fff" /> : null}
            {loading ? 'Loading…' : 'Shuffle'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999' }}
          >
            &times;
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {error && (
            <div style={{ color: '#c62828', padding: 12, fontSize: 13 }}>{error}</div>
          )}
          {!error && !loading && students.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              No students found in {SAMPLE_SEASON}.
            </div>
          )}
          {viewMode === 'by_student' && students.map((student) => (
            <div
              key={student.student_image_id}
              style={{ marginBottom: 20, borderBottom: '1px solid #eee', paddingBottom: 16 }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#333' }}>
                {student.student_name || `Student ${student.student_image_id}`}
                {' '}
                <span style={{ color: '#999', fontWeight: 400 }}>({student.folder})</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {nonBuyAllSlots.map((slot) => {
                  const look = (looks || []).find((l) => l.id === slot.look_template_id);
                  const bgFromMap = slot.background_id ? (backgroundsById || {})[slot.background_id] : null;
                  const backgroundUrl = slot.background_image_url || bgFromMap?.image_url || null;
                  const overlayKey = look?.overlay_s3_path && look?.overlay_filename
                    ? `${look.overlay_s3_path}${look.overlay_s3_path.endsWith('/') ? '' : '/'}${look.overlay_filename}`
                    : null;
                  return (
                    <BatchCell
                      key={slot.slot_id}
                      slot={slot}
                      look={look}
                      student={student}
                      backgroundUrl={backgroundUrl}
                      overlayUrl={overlayKey ? overlayUrls[overlayKey] : null}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {viewMode === 'by_look' && nonBuyAllSlots.map((slot, idx) => {
            const look = (looks || []).find((l) => l.id === slot.look_template_id);
            const bgFromMap = slot.background_id ? (backgroundsById || {})[slot.background_id] : null;
            const backgroundUrl = slot.background_image_url || bgFromMap?.image_url || null;
            const overlayKey = look?.overlay_s3_path && look?.overlay_filename
              ? `${look.overlay_s3_path}${look.overlay_s3_path.endsWith('/') ? '' : '/'}${look.overlay_filename}`
              : null;
            const heading = look
              ? `Look ${idx + 1}: ${look.name || '(unnamed)'}${look.pose_number != null ? ` — Pose ${look.pose_number}` : ''}`
              : `Look ${idx + 1}: (no look assigned)`;
            return (
              <div
                key={slot.slot_id}
                style={{ marginBottom: 20, borderBottom: '1px solid #eee', paddingBottom: 16 }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#333' }}>
                  {heading}
                  {' '}
                  <span style={{ color: '#999', fontWeight: 400 }}>({slot.slot_type})</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {students.map((student) => (
                    <div key={student.student_image_id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <BatchCell
                        slot={slot}
                        look={look}
                        student={student}
                        backgroundUrl={backgroundUrl}
                        overlayUrl={overlayKey ? overlayUrls[overlayKey] : null}
                      />
                      <div style={{ fontSize: 10, color: '#999', maxWidth: 200, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {student.student_name || `Student ${student.student_image_id}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BatchTest;
